import { BillingActivity } from "types/application";
import moment from "moment";
import logoSquare from "shared/branding/logo.svg";
import { formatToCurrency } from "shared/utils";
import { FormatAddress } from "./FormatAddress";
import { CaliforniaRows } from "./CaliforniaRows";
import { NonCaliforniaRows } from "./NonCaliforniaRows";

export const Receipt = ({
  billingActivities,
  primaryActivity
}: {
  billingActivities: BillingActivity[];
  primaryActivity: BillingActivity;
}) => {
  const isCaliforniaAddress = primaryActivity.metadata.one_line_address
    ? primaryActivity.metadata.one_line_address.toLowerCase().includes(" ca") ||
      primaryActivity.metadata.one_line_address
        .toLowerCase()
        .includes(" california")
    : false;

  // Calculate totals from itemized expenses that are not 0 and not the application fee
  const totalExpenses = billingActivities
    .filter(
      (item) =>
        item.amount.cents !== 0 && item.category !== "applicant_application_fee"
    )
    .reduce((sum, item) => sum + Math.abs(item.amount.cents), 0);

  const applicationFeeAmount = billingActivities
    .filter((activity) => activity.category === "applicant_application_fee")
    .reduce((sum, activity) => sum + activity.amount.cents, 0);

  const laborCostAmount = Math.max(0, applicationFeeAmount - totalExpenses);

  const currentDate = moment().format("MMM DD, YYYY");

  return (
    <div>
      <div className="mx-auto px-12 py-8">
        {/* Logo Section */}
        <img
          src={logoSquare}
          alt="Boom Logo"
          className="h-6 w-auto mb-6"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />

        {/* Receipt Title */}
        <h1 className="text-center text-base font-bold text-content-1000 mb-4">
          Receipt
        </h1>

        {/* Header Information */}
        <div className="flex justify-between mb-8">
          <div className="flex-1">
            <p className="text-xs font-bold text-content-1000 mb-2">
              Boompay Inc.
            </p>
            <FormatAddress
              oneLineAddress={primaryActivity.metadata.one_line_address}
            />
          </div>

          <div className="flex-1 text-right text-xs">
            <p className="mb-1 flex justify-end text-xxs font-bold">
              <span className="text-content-1000 mr-2">Receipt #:</span>
              <span className="text-content-300">
                {primaryActivity.id || "N/A"}
              </span>
            </p>
            <p className="mb-1 flex justify-end text-xxs font-bold">
              <span className="text-content-1000 mr-2">Receipt date:</span>
              <span className="text-content-300">{currentDate}</span>
            </p>
            <p className="mb-1 flex justify-end text-xxs font-bold">
              <span className="text-content-1000 mr-2">Applicant:</span>
              <span className="text-content-300">
                {primaryActivity.metadata.name || "N/A"}
              </span>
            </p>
            <p className="mb-1 flex justify-end text-xxs font-bold">
              <span className="text-content-1000 mr-2">
                Application fee paid:
              </span>
              <span className="text-content-300">
                {formatToCurrency(
                  {
                    cents: applicationFeeAmount,
                    currency: "USD"
                  },
                  { minimumFractionDigits: 2 }
                )}
              </span>
            </p>
            <p className="mb-1 flex justify-end text-xxs font-bold">
              <span className="text-content-1000 mr-2">
                Application fee paid date:
              </span>
              <span className="text-content-300">
                {primaryActivity.created_at
                  ? new Date(primaryActivity.created_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "2-digit"
                      }
                    )
                  : currentDate}
              </span>
            </p>
          </div>
        </div>

        {/* Screening Expenses */}
        <div className="mb-4">
          <h2 className="text-xs font-bold text-content-1000 my-4">
            {isCaliforniaAddress ? "Screening expenses" : "Summary"}
          </h2>

          <table className="w-full text-xxs table-fixed border-b border-alpha-40">
            <thead className="border-b border-alpha-40">
              <tr className="text-right font-normal text-content-300">
                <th className="text-left pl-2">Item</th>
                <th>Quantity</th>
                <th>Item price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {isCaliforniaAddress ? (
                <CaliforniaRows billingActivities={billingActivities} />
              ) : (
                <NonCaliforniaRows
                  applicationFeeAmount={applicationFeeAmount}
                />
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div className="mt-4">
            {isCaliforniaAddress ? (
              <>
                <p className="flex justify-end items-center mb-1 font-medium text-xxs text-content-400">
                  <span className="mr-4">Total expenses</span>
                  <span className="w-16 text-right">
                    {formatToCurrency(
                      {
                        cents: totalExpenses,
                        currency: "USD"
                      },
                      { minimumFractionDigits: 2 }
                    )}
                  </span>
                </p>
                <p className="flex justify-end items-center mb-1 font-medium text-xxs text-content-400">
                  <span className="mr-4">Labor costs</span>
                  <span className="w-16 text-right">
                    {formatToCurrency(
                      {
                        cents: laborCostAmount,
                        currency: "USD"
                      },
                      { minimumFractionDigits: 2 }
                    )}
                  </span>
                </p>

                <div className="space-y-1 mb-4 text-content-1000 text-xs font-bold">
                  <p className="flex justify-end items-center">
                    <span className="mr-4">Application cost</span>
                    <span className="w-16 text-right">
                      {formatToCurrency(
                        {
                          cents: applicationFeeAmount,
                          currency: "USD"
                        },
                        { minimumFractionDigits: 2 }
                      )}
                    </span>
                  </p>
                  <p className="flex justify-end items-center">
                    <span className="mr-4">Application fee collected</span>
                    <span className="w-16 text-right">
                      {formatToCurrency(
                        {
                          cents: applicationFeeAmount,
                          currency: "USD"
                        },
                        { minimumFractionDigits: 2 }
                      )}
                    </span>
                  </p>
                  <p className="flex justify-end items-center">
                    <span className="mr-4">Applicant owes</span>
                    <span className="w-16 text-right">
                      {formatToCurrency(
                        {
                          cents: 0,
                          currency: "USD"
                        },
                        { minimumFractionDigits: 2 }
                      )}
                    </span>
                  </p>
                </div>
              </>
            ) : (
              <div className="flex justify-end items-center font-bold text-content-1000">
                <span className="mr-4">Total</span>
                <span className="w-16 text-right">
                  {formatToCurrency(
                    {
                      cents: applicationFeeAmount,
                      currency: "USD"
                    },
                    { minimumFractionDigits: 2 }
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
